import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  SwapAuthForAccessTokens
} from '../../models/login';
import { LoginAuthorizationService } from '../../services/login/data-services/login-authorization/login-authorization-service';
import { LoginPostWithFingerPrintService } from '../../services/login/data-services/login-post-with-fingerprint/login-post-with-fingerprint-service';
import { SwapAccessTokenService } from '../../services/login/data-services/swap-access-token/swap-access-token-service';
import {
  checkRequestInput,
  checkRequestIsDefined,
  isCompletedStatus,
  isUserNamePasswordRequired,
  returnAuthorizationResponseCode
} from '../../utils/login/initiate-login-utils';
import { LoginRememberMeErrorUtilService } from '../../utils/login/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../../utils/login/login-remember-me-utils.service';
import { PostWithCredentialsFacadeService } from './post-with-credentials-facade.service';

@Injectable({
  providedIn: 'root'
})
export class InitiateLoginFacadeExtendedHelperService {
  retryCount = 0;
  loginAuthorization = inject(LoginAuthorizationService);
  loginPostWithFingerPrint = inject(LoginPostWithFingerPrintService);
  loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  postWithCredentialsFacade = inject(PostWithCredentialsFacadeService);
  loginRememberMeErrorUtil = inject(LoginRememberMeErrorUtilService);
  swapAccessToken = inject(SwapAccessTokenService);

  private isAccessTokenResponse(
    response:
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | AuthAccessTokenDetails
      | string
      | boolean
  ): response is AuthAccessTokenDetails {
    return (
      typeof response === 'object' &&
      response !== null &&
      'access_token' in response
    );
  }

  handleCredentialsResponse(
    response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput | undefined
  ): Observable<
    | LoginAuthWithCredentialsResponse
    | SwapAuthForAccessTokens
    | AuthAccessTokenDetails
    | string
    | boolean
  > {
    if (this.isAccessTokenResponse(response)) {
      return of(response);
    } else if (
      isUserNamePasswordRequired(
        response as LoginAuthWithCredentialsResponse,
        loginOAuthParams
      )
    ) {
      return checkRequestInput(
        loginOAuthParams,
        response as LoginAuthWithCredentialsResponse,
        this.postWithCredentialsFacade,
        checkRequestIsDefined(requestInput)
      );
    } else if (
      isCompletedStatus(response as SwapAuthForAccessTokens, loginOAuthParams)
    ) {
      return this.swapAccessToken.swapAuthCodeForToken(
        loginOAuthParams,
        returnAuthorizationResponseCode(response as SwapAuthForAccessTokens)
      );
    } else {
      return throwError(response);
    }
  }
}
